import type { OverridableItemField } from '@fall26/shared'
import type { ReactNode } from 'react'

/** The small shared pieces of the review screen. */

/**
 * Buttons.
 *
 * `primary` is deliberately NOT the accent colour. Burnt orange means "today"
 * and "the thing you are on" everywhere else in this app, and a screen with
 * fifteen orange buttons on it would spend that meaning in an afternoon. The
 * affirmative action is the solid one; the accent stays out of it.
 */
export function Action({
  onClick,
  children,
  tone = 'quiet',
  disabled = false,
  busy = false,
}: {
  onClick: () => void
  children: ReactNode
  tone?: 'primary' | 'quiet' | 'danger'
  disabled?: boolean
  busy?: boolean
}) {
  const tones = {
    primary: 'border-[var(--text)] bg-[var(--text)] text-[var(--text-inverted)]',
    quiet: 'border-[var(--border)] bg-[var(--surface)] text-[var(--text-muted)]',
    danger: 'border-[var(--overdue)]/40 bg-[var(--overdue-wash)] text-[var(--overdue)]',
  } as const

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      className={`tap tabular shrink-0 rounded-[var(--radius)] border px-3 text-[11.5px] uppercase tracking-[0.06em] transition-opacity disabled:opacity-40 ${tones[tone]}`}
    >
      {busy ? '…' : children}
    </button>
  )
}

export function KindBadge({ kind }: { kind: string }) {
  const label: Record<string, string> = {
    add: 'new',
    link: 'maybe a duplicate',
    update: 'changed',
    stale: 'gone from Canvas',
  }
  const tone: Record<string, string> = {
    add: 'bg-[var(--surface-sunken)] text-[var(--text-muted)]',
    link: 'bg-[var(--soon-wash)] text-[var(--soon)]',
    update: 'bg-[var(--soon-wash)] text-[var(--soon)]',
    stale: 'bg-[var(--overdue-wash)] text-[var(--overdue)]',
  }
  return (
    <span
      className={`tabular rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] ${tone[kind] ?? tone['add']}`}
    >
      {label[kind] ?? kind}
    </span>
  )
}

export function Facts({ children }: { children: ReactNode }) {
  return <dl className="mt-2 space-y-1">{children}</dl>
}

export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="tabular w-16 shrink-0 text-[11px] uppercase tracking-[0.06em] text-[var(--text-subtle)]">
        {label}
      </dt>
      <dd className="min-w-0 text-[12.5px] leading-snug text-[var(--text-muted)]">{children}</dd>
    </div>
  )
}

/** Human names for the fields a diff can carry. */
export const FIELD_LABEL: Record<OverridableItemField, string> = {
  title: 'Title',
  detail: 'Detail',
  dueDate: 'Due date',
  dueTime: 'Due time',
  dueLabel: 'Due label',
  category: 'Category',
  categoryLabel: 'Category name',
  dateConfirmed: 'Date confirmed',
  note: 'Note',
  room: 'Room',
  bring: 'Bring',
  gate: 'Gate',
  optional: 'Optional',
  reminderOffsetsMin: 'Reminders',
}

/**
 * A field value, as text.
 *
 * The server has already formatted these; the only thing left to decide is what
 * an ABSENCE looks like. It renders as "nothing", never as an empty gap — half
 * the point of a diff is seeing that Canvas wants to CLEAR something, and a
 * blank cell reads as "no change".
 */
export function fieldText(display: string): string {
  return display === '' ? 'nothing' : display
}
